import * as THREE from 'three';
import * as marietta3d from '../../../marietta3d';

// 計算用utils
class CalculationUtil {
  /**
  * 2点間の角度を求める
  */
  public static getAngleBetween(vec1: THREE.Vector2, vec2: THREE.Vector2): number {
    // 底辺の角度
    const angle: number = Math.atan2(vec2.x - vec1.x, -(vec2.y - vec1.y)) * (180 / Math.PI) - 90;
    return angle;
  }

  /*------------------------------------------------------
  * method : AngleRad
  * vec1を中心として、Ｘ軸＋方向と点 p のなす角を求める。
  * （反時計周り：プラス、戻り値：ラジアン）
  */
  public static AngleRad(vec1: THREE.Vector2, vec2: THREE.Vector2): number {
    let r = vec1.clone().distanceTo(vec2.clone());
    if (r < marietta3d.AutoGen.utils.ALMOST_ZERO) {
      return 0.0;
    }
    const dx = (vec2.x - vec1.x);
    const dy = (vec2.y - vec1.y);
    r = Math.atan2(dy, dx);
    if (r < 0.0) {
      r += (2 * Math.PI);
    }
    return r;
  }

  /*------------------------------------------------------
  * method : Point.Angle
  *
  * vec1を中心として、Ｘ軸＋方向と点 p のなす角を求める。
  * （反時計周り：プラス、戻り値：度）
  *
  */
  public static Angle(vec1: THREE.Vector2, vec2: THREE.Vector2): number {
    const r = this.AngleRad(vec1, vec2);
    return ((r / Math.PI) * 180);
  }

  /*------------------------------------------------------
  * method : RotationRad
  *
  * vec2を中心として、vec1をrad分だけ回転させる
  * （反時計周り：プラス）rad : ラジアン
  *
  */
  public static RotationRad(vec1: THREE.Vector2, vec2: THREE.Vector2, rad: number) {
    const newVec: THREE.Vector2 = vec1;
    const tx = newVec.x - vec2.x;
    const ty = newVec.y - vec2.y;
    const c = Math.cos(rad);
    const s = Math.sin(rad);
    newVec.x = (tx * c - ty * s) + vec2.x;
    newVec.y = (tx * s + ty * c) + vec2.y;
  }

  /*------------------------------------------------------
  * method : Rotation
  *
  * vec2を中心として、vec1をang分だけ回転させる
  * （反時計周り：プラス）ang : 度
  *
  */
  public static Rotation(vec1: THREE.Vector2, vec2: THREE.Vector2, ang: number) {
    const rad = (ang / 180) * Math.PI;
    this.RotationRad(vec1, vec2, rad);
  }

  /*------------------------------------------------------
  * method : Swap
  * 入れ替え
  */
  public static Swap(vec1: THREE.Vector2, vec2: THREE.Vector2) {
    const newVec1: THREE.Vector2 = vec1;
    const newVec2: THREE.Vector2 = vec2;
    [newVec1.x, newVec2.x] = [newVec2.x, newVec1.x];
    [newVec1.y, newVec2.y] = [newVec2.y, newVec1.y];
  }

  /*------------------------------------------------------
   * method : Copy
   * 値の明示的なコピー
   */
  public static Copy(vec1: THREE.Vector2, vec2: THREE.Vector2) {
    const newVec: THREE.Vector2 = vec1;
    newVec.x = vec2.x;
    newVec.y = vec2.y;
  }

  /*------------------------------------------------------
   * ベクターの単位化（正規化）。
   * 引数のベクトルを正規化して this に格納する
   * dist＜ALMOST_ZERO  の時、 ゼロベクトル(0,0)の値を返す。
   */
  public static UnitVector(vec1: THREE.Vector2, vec2: THREE.Vector2): number {
    let dot = Math.sqrt(vec2.clone().dot(vec2));
    const newVec: THREE.Vector2 = vec1;

    if (dot < marietta3d.AutoGen.utils.ALMOST_ZERO) {
      dot = 0.0;
      newVec.x = 0.0;
      newVec.y = 0.0;
    } else {
      newVec.x = vec2.x / dot;
      newVec.y = vec2.y / dot;
    }
    return dot;
  }

  /*------------------------------------------------------
   * ２点を与えて単位ベクターと距離を求める
   * （vec1に単位ベクトルが入る）
   */
  public static UnitVectorZ2(vec1: THREE.Vector2, vec2: THREE.Vector2, vec3: THREE.Vector2): number {
    const subVec: THREE.Vector2 = vec2.clone().sub(vec3);
    const dot = CalculationUtil.UnitVector(vec1, subVec);
    return dot;
  }

  /*------------------------------------------------------
   * 単位直線と点との関係値を求める
   *
   *  vec2: 始点
   *  vec3:方向ベクトル（単位ベクトル）
   *  vec1:調べる点
   *  vec4:戻り値 vec4.x:内積値、vec4.y:外積値
   */
  public static IsPtOnEdgeVector(vec1: THREE.Vector2, vec2: THREE.Vector2, vec3: THREE.Vector2, vec4: THREE.Vector2) {
    const newVec = vec4;
    const sub: THREE.Vector2 = vec1.clone().sub(vec2);
    newVec.x = vec3.clone().dot(sub);
    // crossがないため代用(THREE.jsのバージョンが古いせい？)
    newVec.y = (vec3.x * sub.y - vec3.y * sub.x);
  }

  /**
   * digitsで指定した小数点以下を四捨五入
   * @param value
   * @param digits
   * @returns
   */
  public static roundValue(value: number, digits: number = 3): number {
    const scale = 10 ** digits;
    return Math.round(value * scale) / scale;
  }
}
export default CalculationUtil;
